import React, { type SVGProps, useId, useState } from 'react';

import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Heading,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  type UseDisclosureReturn,
  chakra,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { type UseQueryResult } from '@tanstack/react-query';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { RiArrowRightSLine } from 'react-icons/ri';
import { useSearchParams } from 'react-router-dom';

import {
  Form,
  FormField,
  IconDotsVertical,
  IconTrash,
  formatDate,
} from '@/components';
import {
  DataList,
  DataListCell,
  DataListEmptyState,
  DataListErrorState,
  DataListLoadingState,
  DataListRow,
  DataListText,
  DataListTextHeader,
} from '@/components/DataList';
import { useDateRangeQueryState } from '@/components/Filters/Date/hooks';
import { dateRangeDefaultValue } from '@/components/Popover';
import { SearchInput } from '@/components/SearchInput';
import { LinkAdmin } from '@/features/admin';
import {
  useGetInterviewsQueries,
  useGetTemplateInterviewsQueries,
  useRemoveInterviewMutation,
} from '@/features/admin-interviews/useAdminInterviewsQueries';
import {
  AdminLayoutPage,
  AdminLayoutPageContent,
} from '@/features/admin/AdminLayoutPage';
import {
  type InterviewBasicDtoPagedResponseDto,
  type Status,
} from '@/services/api';
import {
  parseAsInteger,
  parseAsJson,
  parseAsString,
  useQueryState,
} from '@/utils/useQueryState';

import {
  formatTime,
  mergeDateAndTime,
  useAssignTemplateInterviewMutation,
  useDeleteTemplateInterviewMutation,
  useGetInterviewTemplateCompanyList,
  useReplicateInterviewMutation,
} from '../admin-new-interview';
import { useGetAccountQuery } from '../auth';
import { type DeleteButtonProps } from '../candidate/types';
import { statusColor } from '../interview/statusColor';
import { statusLabel } from '../interview/statusLabel';
import {
  AdminInterviewsFilters,
  type StatusItemsObj,
  statusFilterItems,
} from './AdminInterviewsFilters';

const statusDefaultValue = statusFilterItems.reduce((a, c) => {
  a[c.name] = false;
  return a;
}, {} as StatusItemsObj);

type Tabs = 'interviews' | 'templates' | 'createdTemplates';

export const PageAdminInterviews = () => {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tabs>(
    searchParams.get('tab') === 'templates' ? 'createdTemplates' : 'interviews'
  );

  const [searchTerm, setSearchTerm] = useQueryState(
    'q',
    parseAsString.withDefault('').withOptions({ clearOnDefault: true })
  );

  const [page, setPage] = useQueryState(
    'page',
    parseAsInteger.withDefault(1).withOptions({
      clearOnDefault: true,
    })
  );

  const [templatePage, setTemplatePage] = useQueryState(
    'templatePage',
    parseAsInteger.withDefault(1).withOptions({
      clearOnDefault: true,
    })
  );

  const [createdTemplatePage, setCreatedTemplatePage] = useQueryState(
    'createdTemplatePage',
    parseAsInteger.withDefault(1).withOptions({
      clearOnDefault: true,
    })
  );

  const [status, setStatus] = useQueryState<StatusItemsObj>(
    'status',
    parseAsJson<StatusItemsObj>()
      .withDefault(statusDefaultValue)
      .withOptions({ clearOnDefault: true })
  );

  const [dateRangeSearchTerm, setDateRangeSearchTerm] =
    useDateRangeQueryState();

  const account = useGetAccountQuery();

  const interviewsQuery = useGetInterviewsQueries({
    size: 10,
    page,
    q: searchTerm,
    startDateFrom: dateRangeSearchTerm.from,
    startDateTo: dateRangeSearchTerm.to,
    status: Object.keys(status)
      .filter((k) => status[k as Status])
      .map((k) => k as Status),
    isTemplateInterview: false,
  });

  const interviewsTemplatesQuery = useGetInterviewsQueries({
    size: 10,
    page,
    q: searchTerm,
    startDateFrom: dateRangeSearchTerm.from,
    startDateTo: dateRangeSearchTerm.to,
    status: Object.keys(status)
      .filter((k) => status[k as Status])
      .map((k) => k as Status),
    isTemplateInterview: true,
  });

  const createdTemplates = useGetTemplateInterviewsQueries(
    {
      size: 10,
      page: templatePage,
      q: searchTerm,
    },
    {
      enabled: account.data?.isSynapsAdmin,
    }
  );

  const { t } = useTranslation(['auth', 'interview', 'common', 'candidate']);

  const clearAllFilters = () => {
    setDateRangeSearchTerm(dateRangeDefaultValue);
    setStatus(statusDefaultValue);
    setPage(1);
    setSearchTerm('');
  };

  return (
    <AdminLayoutPage>
      <AdminLayoutPageContent>
        <DataList>
          <DataListRow
            align="center"
            py="20px"
            px="24px"
            justify="space-between"
          >
            <Flex>
              <Heading fontSize="2xl">
                {`${t('interview:videoInterview.videoInterviews')} (${(createdTemplates.data?.total ?? 0) + (interviewsQuery.data?.total ?? 0)})`}
              </Heading>
            </Flex>
          </DataListRow>
          <Tabs defaultIndex={searchParams.get('tab') === 'templates' ? 2 : 0}>
            <DataListRow
              align="center"
              py="20px"
              px="24px"
              justify="space-between"
            >
              <TabList
                columnGap={2}
                bg="gray.25"
                p={1}
                borderWidth={1}
                borderColor="gray.50"
                rounded="xl"
              >
                <InterviewButtonTab
                  onClick={() => setTab('interviews')}
                  title={t('candidate:settings.myInterviews')}
                  total={interviewsQuery.data?.total}
                  isActive={tab === 'interviews'}
                />

                <InterviewButtonTab
                  onClick={() => setTab('templates')}
                  title={t('interview:myReadyInterviews')}
                  total={interviewsTemplatesQuery.data?.total}
                  isActive={tab === 'templates'}
                />

                {account.data?.isSynapsAdmin && (
                  <InterviewButtonTab
                    onClick={() => setTab('createdTemplates')}
                    title={t('interview:readyInterviews')}
                    total={createdTemplates.data?.total}
                    isActive={tab === 'createdTemplates'}
                  />
                )}
              </TabList>

              <HStack>
                {tab === 'createdTemplates' && account.data?.isSynapsAdmin && (
                  <LinkAdmin to="/interviews/new?isAdminTemplate=true">
                    <Button px={5}>{t('interview:createTemplate')}</Button>
                  </LinkAdmin>
                )}
                <HStack>
                  <AdminInterviewsFilters
                    status={status}
                    setStatus={setStatus}
                    dateRangeSearchTerm={dateRangeSearchTerm}
                    setDateRangeSearchTerm={setDateRangeSearchTerm}
                    clearAllFilters={clearAllFilters}
                    setPage={setPage}
                  />

                  <SearchInput
                    onChange={(value) => {
                      setPage(1);
                      setSearchTerm(value ?? null);
                    }}
                    value={searchTerm}
                    delay={1000}
                  />
                </HStack>
              </HStack>
            </DataListRow>
            <DataListRow>
              <DataListCell flex={2}>
                <DataListTextHeader>
                  {t(
                    'interview:videoInterview.interviewName'
                  ).toAppLocaleUpperCase()}
                </DataListTextHeader>
              </DataListCell>
              <DataListCell>
                <DataListTextHeader>
                  {t(
                    'interview:videoInterview.creationDate'
                  ).toAppLocaleUpperCase()}
                </DataListTextHeader>
              </DataListCell>
              <DataListCell>
                <DataListTextHeader>
                  {t(
                    'interview:videoInterview.numberOfQuestions'
                  ).toAppLocaleUpperCase()}
                </DataListTextHeader>
              </DataListCell>
              {tab !== 'createdTemplates' && (
                <DataListCell>
                  <DataListTextHeader>
                    {t('common:detail.status').toAppLocaleUpperCase()}
                  </DataListTextHeader>
                </DataListCell>
              )}
              <DataListCell w="20%">
                <DataListTextHeader>
                  {t(
                    'interview:videoInterview.transactions'
                  ).toAppLocaleUpperCase()}
                </DataListTextHeader>
              </DataListCell>
            </DataListRow>
            <TabPanels>
              <TabPanel>
                <InterviewList
                  page={page}
                  setPage={setPage}
                  interviewsQuery={interviewsQuery}
                />
              </TabPanel>
              <TabPanel>
                <InterviewList
                  page={templatePage}
                  setPage={setTemplatePage}
                  interviewsQuery={interviewsTemplatesQuery}
                  isTemplate
                />
              </TabPanel>
              <TabPanel>
                <InterviewTemplateList
                  page={createdTemplatePage}
                  setPage={setCreatedTemplatePage}
                  interviewsQuery={createdTemplates}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </DataList>
      </AdminLayoutPageContent>
    </AdminLayoutPage>
  );
};

type InterviewButtonTabProps = {
  title: string;
  onClick: () => void;
  isActive: boolean;
  total?: number;
};

const InterviewButtonTab: React.FC<InterviewButtonTabProps> = ({
  title,
  onClick,
  total,
  isActive,
}) => {
  return (
    <Tab
      rounded="lg"
      _selected={{
        color: 'primary.500',
        bg: 'white',
        borderColor: 'gray.100',
        borderWidth: 1,
      }}
      height={10}
      as={Button}
      variant="outline"
      color="gray.600"
      boxShadow="none"
      onClick={onClick}
    >
      <Text onClick={onClick}>
        {title}
        {!!total && (
          <Text
            ml={2}
            as="span"
            color={isActive ? 'primary.700' : 'gray.600'}
            bg="primary.50"
            p={1}
            px={2}
            rounded="full"
            fontSize="sm"
          >
            {total}
          </Text>
        )}
      </Text>
    </Tab>
  );
};

interface InterviewListProps {
  interviewsQuery: UseQueryResult<InterviewBasicDtoPagedResponseDto>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  isTemplate?: boolean;
}

const InterviewList: React.FC<InterviewListProps> = ({
  interviewsQuery,
  page,
  setPage,
  isTemplate,
}) => {
  const [openPopoverId, setOpenPopoverId] = useState<string>();
  const { t } = useTranslation(['auth', 'interview', 'common']);
  const ChRiArrowRightSLine = chakra(RiArrowRightSLine);
  const removeInterviewMutation = useRemoveInterviewMutation();

  if (interviewsQuery.isLoading) {
    return <DataListLoadingState />;
  }

  if (interviewsQuery.isError) {
    return (
      <DataListErrorState
        title={t('auth:users.feedbacks.loadingUserError.title')}
        retry={() => interviewsQuery.refetch()}
      />
    );
  }

  if (interviewsQuery.isSuccess && !interviewsQuery.data?.data?.length) {
    return (
      <DataListEmptyState
        label={
          isTemplate
            ? t('interview:noInterviewTemplates')
            : t('interview:noInterview')
        }
      />
    );
  }

  return (
    <>
      {interviewsQuery.data?.data?.map((interview) => (
        <DataListRow key={interview.id} withHover>
          <DataListCell flex={2}>
            <DataListText color="gray.900">
              <LinkAdmin
                to={
                  isTemplate
                    ? `/interviews/${interview.id}?isTemplate=true`
                    : `/interviews/${interview.id}`
                }
              >
                {interview.title}
              </LinkAdmin>
            </DataListText>
          </DataListCell>
          <DataListCell>
            <DataListText color="text-dimmed">
              {formatDate(interview.createdAt)}
            </DataListText>
          </DataListCell>
          <DataListCell>
            <DataListText color="text-dimmed">
              {t('common:detail.question', {
                count: interview.questionCount,
              })}
            </DataListText>
          </DataListCell>
          <DataListCell>
            <Badge colorScheme={statusColor(interview.status).schema}>
              {statusLabel(interview.status)}
            </Badge>
          </DataListCell>
          <DataListCell px={0} py={0} w="20%" flexDir="row">
            <DataListCell flex={1}>
              <LinkAdmin
                to={
                  isTemplate
                    ? `/interviews/${interview.id}?isTemplate=true`
                    : `/interviews/${interview.id}`
                }
              >
                <Button
                  h="9"
                  fontSize="sm"
                  rightIcon={<ChRiArrowRightSLine size="20px" />}
                  variant="@secondary"
                >
                  {t('common:detail.detail')}
                </Button>
              </LinkAdmin>
            </DataListCell>

            {!isTemplate && (
              <DataListCell h="full" w="14" align="center">
                <Menu key={interview.id}>
                  <MenuButton
                    as={IconButton}
                    aria-label="Options"
                    icon={<IconDotsVertical mx={2} fontSize={20} />}
                    variant="unstyled"
                    boxShadow="none"
                  />
                  <MenuList py={0} key={interview.id}>
                    <MenuItem borderTopRadius={5} p={0}>
                      <CopyItem
                        defaultTitle={interview.title ?? ''}
                        interviewId={interview.id}
                      />
                    </MenuItem>
                    <Box _hover={{ backgroundColor: 'gray.100' }} px={2.5}>
                      <DeleteInterviewButton
                        onDelete={async () => {
                          if (!interview.id) return;
                          await removeInterviewMutation.mutateAsync({
                            interviewsId: interview.id,
                          });
                        }}
                        isLoading={removeInterviewMutation.isPending}
                        headerTranslationKey={t('interview:deleteInterview')}
                        bodyTranslationKey={t(
                          'interview:deleteInterviewPopupBody'
                        )}
                        itemId={interview.id}
                        openPopoverId={openPopoverId}
                        setOpenPopoverId={setOpenPopoverId}
                      />
                    </Box>
                  </MenuList>
                </Menu>
              </DataListCell>
            )}
          </DataListCell>
        </DataListRow>
      ))}
      {interviewsQuery.data?.totalPages && (
        <DataListRow>
          <DataListCell flex={1} justify="center" align="center">
            <Pagination
              currentPage={page}
              totalPages={interviewsQuery.data?.totalPages}
              onPageChange={(page) => setPage(page)}
            />
          </DataListCell>
        </DataListRow>
      )}
    </>
  );
};

const InterviewTemplateList: React.FC<InterviewListProps> = ({
  interviewsQuery,
}) => {
  const [openPopoverId, setOpenPopoverId] = useState<string>();
  const { t } = useTranslation(['auth', 'interview', 'common']);
  const ChRiArrowRightSLine = chakra(RiArrowRightSLine);
  const removeInterviewTemplateMutation = useDeleteTemplateInterviewMutation();

  if (interviewsQuery.isLoading) {
    return <DataListLoadingState />;
  }

  if (interviewsQuery.isSuccess && !interviewsQuery.data?.data?.length) {
    return <DataListEmptyState label={t('interview:noInterviewTemplates')} />;
  }

  return interviewsQuery.data?.data?.map((interview) => (
    <DataListRow key={interview.id} withHover>
      <DataListCell flex={2}>
        <DataListText color="gray.900">
          <LinkAdmin to={`/interviews/${interview.id}`}>
            {interview.title}
          </LinkAdmin>
        </DataListText>
      </DataListCell>
      <DataListCell>
        <DataListText color="text-dimmed">
          {formatDate(interview.createdAt)}
        </DataListText>
      </DataListCell>
      <DataListCell>
        <DataListText color="text-dimmed">
          {t('common:detail.question', {
            count: interview.questionCount,
          })}
        </DataListText>
      </DataListCell>
      <DataListCell px={0} py={0} w="20%" flexDir="row">
        <DataListCell flex={1}>
          <LinkAdmin to={`/interviews/${interview.id}?isAdminTemplate=true`}>
            <Button
              h="9"
              fontSize="sm"
              rightIcon={<ChRiArrowRightSLine size="20px" />}
              variant="@secondary"
            >
              {t('common:detail.detail')}
            </Button>
          </LinkAdmin>
        </DataListCell>

        <DataListCell h="full" w="14" align="center">
          <Menu key={interview.id}>
            <MenuButton
              as={IconButton}
              aria-label="Options"
              icon={<IconDotsVertical mx={2} fontSize={20} />}
              variant="unstyled"
              boxShadow="none"
            />
            <MenuList py={0} key={interview.id}>
              <MenuItem borderTopRadius={5} p={0}>
                <CopyItem
                  defaultTitle={interview.title ?? ''}
                  interviewId={interview.id}
                  isTemplate
                />
              </MenuItem>
              <Box _hover={{ backgroundColor: 'gray.100' }} px={2.5}>
                <DeleteInterviewButton
                  onDelete={async () => {
                    if (!interview.id) return;
                    await removeInterviewTemplateMutation.mutateAsync(
                      interview.id
                    );
                  }}
                  isLoading={removeInterviewTemplateMutation.isPending}
                  headerTranslationKey={t('interview:deleteInterview')}
                  bodyTranslationKey={t('interview:deleteInterviewPopupBody')}
                  itemId={interview.id}
                  openPopoverId={openPopoverId}
                  setOpenPopoverId={setOpenPopoverId}
                />
              </Box>
            </MenuList>
          </Menu>
        </DataListCell>
      </DataListCell>
    </DataListRow>
  ));
};

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const renderPageNumbers = () => {
    const pages: Array<React.ReactNode> = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <Button
          key={i}
          size="sm"
          onClick={() => onPageChange(i)}
          variant={i === currentPage ? 'solid' : 'ghost'}
          colorScheme={i === currentPage ? 'brand' : 'gray'}
          mx={1}
        >
          {i}
        </Button>
      );
    }
    return pages;
  };

  return (
    <Flex alignItems="center">
      <Button
        onClick={() => onPageChange(currentPage - 1)}
        isDisabled={currentPage === 1}
        variant="ghost"
        size="sm"
        mx={1}
      >
        <FaChevronLeft />
      </Button>
      <Flex>
        {currentPage > 3 && (
          <>
            <Button
              size="sm"
              variant="ghost"
              mx={1}
              onClick={() => onPageChange(1)}
            >
              1
            </Button>
            <Button size="sm" variant="ghost" mx={1}>
              ...
            </Button>
          </>
        )}
        {renderPageNumbers().slice(
          Math.max(0, currentPage - 3),
          Math.min(totalPages, currentPage + 2)
        )}
        {currentPage < totalPages - 2 && (
          <>
            <Button size="sm" variant="ghost" mx={1}>
              ...
            </Button>
            <Button
              size="sm"
              variant="ghost"
              mx={1}
              onClick={() => onPageChange(totalPages)}
            >
              {totalPages}
            </Button>
          </>
        )}
      </Flex>
      <Button
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        isDisabled={currentPage === totalPages}
        variant="ghost"
        mx={1}
      >
        <FaChevronRight />
      </Button>
    </Flex>
  );
};

const DeleteInterviewButton: React.FC<DeleteButtonProps> = ({
  onDelete,
  isLoading,
  headerTranslationKey,
  bodyTranslationKey,
  itemId,
  openPopoverId,
  setOpenPopoverId,
}) => {
  const deleteModalDisclosure: UseDisclosureReturn = useDisclosure();
  const { t } = useTranslation(['interview', 'common']);

  const isPopoverOpen = openPopoverId === itemId;

  const onCancel = () => {
    setOpenPopoverId?.(undefined);
    deleteModalDisclosure.onClose();
  };

  return (
    <Popover
      isOpen={isPopoverOpen}
      onOpen={() => {
        setOpenPopoverId?.(itemId);
        deleteModalDisclosure.onOpen();
      }}
      onClose={() => {
        setOpenPopoverId?.(undefined);
        deleteModalDisclosure.onClose();
      }}
      placement="right"
    >
      <PopoverTrigger>
        <Button
          variant="unstyled"
          boxShadow="none"
          width="full"
          justifyContent="start"
          columnGap={2}
          display="flex"
          color="red.500"
        >
          <IconTrash fontSize={20} color="red.500" />
          {t('interview:deleteInterview')}
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent>
          <PopoverHeader fontWeight="semibold">
            {headerTranslationKey}
          </PopoverHeader>
          <PopoverBody>{bodyTranslationKey}</PopoverBody>
          <PopoverFooter display="flex" justifyContent="flex-end">
            <ButtonGroup size="sm">
              <Button onClick={onCancel} variant="outline">
                {t('common:actions.cancel')}
              </Button>
              <Button
                colorScheme="red"
                isLoading={isLoading}
                onClick={async () => {
                  await onDelete();
                  deleteModalDisclosure.onClose();
                }}
              >
                {t('common:actions.yes')}
              </Button>
            </ButtonGroup>
          </PopoverFooter>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export interface CopyItemProps {
  defaultTitle: string;
  interviewId: string;
  isTemplate?: boolean;
}

type FormInputs = {
  title: string;
  startDate: Date;
  startTime: string;
  endTime: string;
  endDate: Date;
  companyName?: string;
};

const now = new Date();
const nextWeek = new Date(now);
nextWeek.setDate(nextWeek.getDate() + 7);

export const CopyItem: React.FC<CopyItemProps> = ({
  defaultTitle,
  interviewId,
  isTemplate,
}) => {
  const { t } = useTranslation(['interview', 'common']);
  const [isOpen, setIsOpen] = useState(false);
  const toast = useToast();
  const account = useGetAccountQuery();

  const { mutateAsync: replicateInterview, isPending } =
    useReplicateInterviewMutation({
      onSuccess: async () => {
        setIsOpen(false);
        toast({
          title: t('interview:createNewInterview.interviewCopied'),
          status: 'success',
        });
      },
    });

  const companyList = useGetInterviewTemplateCompanyList({
    enabled: Boolean(isTemplate && account.data?.isSynapsAdmin),
  });

  const assignTemplateToCompany = useAssignTemplateInterviewMutation({
    onSuccess: async () => {
      setIsOpen(false);
      toast({
        title: t('interview:createNewInterview.interviewCopied'),
        status: 'success',
      });
    },
  });

  const onFormSubmit: SubmitHandler<FormInputs> = async (data) => {
    if (isTemplate) {
      await assignTemplateToCompany.mutateAsync({
        interviewTemplateId: interviewId,
        body: {
          title: data.title,
          companyId: data.companyName,
          startDate: mergeDateAndTime(data.startDate, data.startTime),
          endDate: mergeDateAndTime(data.endDate, data.endTime),
        },
      });
      return;
    }

    replicateInterview({
      title: data.title,
      startDate: mergeDateAndTime(data.startDate, data.startTime),
      endDate: mergeDateAndTime(data.endDate, data.endTime),
      interviewId,
    });
  };

  const form = useForm<FormInputs>({
    mode: 'onBlur',
    defaultValues: {
      title: defaultTitle,
      startDate: now,
      startTime: formatTime(now),
      endDate: nextWeek,
      endTime: formatTime(now),
    },
  });

  const {
    register,
    formState: { errors },
  } = form;

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        variant="unstyled"
        display="flex"
        columnGap={2}
        alignItems="center"
        boxShadow="none"
        width="full"
        justifyContent="start"
        px={3}
      >
        <CopyIcon />
        {t('interview:copyInterview')}
      </Button>
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <ModalOverlay />
        <ModalContent margin="auto" borderRadius="xl" overflow="hidden">
          <ModalHeader display="flex" justifyContent="center">
            {t('interview:copyInterview')}
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Form {...form} onSubmit={onFormSubmit}>
              <Stack spacing={5}>
                {account.data?.isSynapsAdmin && isTemplate && (
                  <FormField
                    name="companyName"
                    type="select"
                    label={t('common:companyName')}
                    options={(companyList?.data ?? [])
                      ?.filter((company) => company.name !== undefined)
                      ?.map((company) => ({
                        label: company.name as string,
                        value: company.id,
                      }))}
                  />
                )}
                <FormField
                  name="title"
                  type="text"
                  optionalityHint="required"
                  label={t(
                    'interview:createNewInterview.interviewInformation.interviewTitle'
                  )}
                />
              </Stack>
              <HStack spacing={4} mt={5}>
                <FormField
                  type="date"
                  dayPickerProps={{
                    arePastDaysDisabled: true,
                    placeholder: t(
                      'common:timeAndDate.datePlaceHolder'
                    ).toAppLocaleUpperCase(),
                  }}
                  label={`${t('common:timeAndDate.startDate')} *`}
                  rules={{
                    required: t('common:timeAndDate.startDateRequired'),
                  }}
                  control={form.control}
                  name="startDate"
                />

                <FormControl isInvalid={!!errors.startTime}>
                  <FormLabel htmlFor="startTime">{`${t('common:timeAndDate.startTime')} *`}</FormLabel>
                  <Input
                    id="startTime"
                    type="time"
                    {...register('startTime', {
                      required: t('common:timeAndDate.startTimeRequired'),
                    })}
                  />
                  <FormErrorMessage>
                    {errors.startTime && errors.startTime.message}
                  </FormErrorMessage>
                </FormControl>
              </HStack>
              <HStack spacing={4} mt={2} mb={5}>
                <FormField
                  type="date"
                  label={`${t('common:timeAndDate.endDate')} *`}
                  rules={{
                    required: t('common:timeAndDate.endDateRequired'),
                  }}
                  dayPickerProps={{
                    arePastDaysDisabled: true,
                  }}
                  control={form.control}
                  name="endDate"
                />

                <FormControl isInvalid={!!errors.endTime}>
                  <FormLabel htmlFor="endTime">
                    {t('common:timeAndDate.endTime')}
                  </FormLabel>
                  <Input
                    id="endTime"
                    type="time"
                    {...register('endTime', {
                      required: t('common:timeAndDate.endTimeRequired'),
                    })}
                  />
                  <FormErrorMessage>
                    {errors.endTime && errors.endTime.message}
                  </FormErrorMessage>
                </FormControl>
              </HStack>
              <Button
                mb={2}
                type="submit"
                isLoading={isPending}
                variant="@primary"
              >
                {t('common:actions.copy')}
              </Button>
            </Form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

const CopyIcon = (props: SVGProps<SVGSVGElement>) => {
  const id = useId();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      fill="none"
      viewBox="0 0 20 20"
      {...props}
    >
      <g clipPath={`url(#${id})`}>
        <path
          stroke="#656D75"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M4.16797 12.5003C3.3914 12.5003 3.00311 12.5003 2.69683 12.3735C2.28845 12.2043 1.96399 11.8798 1.79484 11.4715C1.66797 11.1652 1.66797 10.7769 1.66797 10.0003V4.33366C1.66797 3.40024 1.66797 2.93353 1.84962 2.57701C2.00941 2.2634 2.26438 2.00844 2.57798 1.84865C2.9345 1.66699 3.40121 1.66699 4.33464 1.66699H10.0013C10.7779 1.66699 11.1662 1.66699 11.4724 1.79386C11.8808 1.96302 12.2053 2.28747 12.3744 2.69585C12.5013 3.00214 12.5013 3.39042 12.5013 4.16699M10.168 18.3337H15.668C16.6014 18.3337 17.0681 18.3337 17.4246 18.152C17.7382 17.9922 17.9932 17.7372 18.153 17.4236C18.3346 17.0671 18.3346 16.6004 18.3346 15.667V10.167C18.3346 9.23357 18.3346 8.76686 18.153 8.41034C17.9932 8.09674 17.7382 7.84177 17.4246 7.68198C17.0681 7.50033 16.6014 7.50033 15.668 7.50033H10.168C9.23455 7.50033 8.76784 7.50033 8.41132 7.68198C8.09771 7.84177 7.84275 8.09674 7.68296 8.41034C7.5013 8.76686 7.5013 9.23357 7.5013 10.167V15.667C7.5013 16.6004 7.5013 17.0671 7.68296 17.4236C7.84275 17.7372 8.09771 17.9922 8.41132 18.152C8.76784 18.3337 9.23455 18.3337 10.168 18.3337Z"
        />
      </g>
      <defs>
        <clipPath id={id}>
          <path fill="#fff" d="M0 0H20V20H0z" />
        </clipPath>
      </defs>
    </svg>
  );
};
